const ATTEMPT_KEY_PREFIX = "romi-abandoned-cart-attempt";
const memoryAttempts = new Map();

const cleanPart = (value, maxLength = 100) =>
  String(value || "")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9-]/g, "-")
    .replace(/-+/g, "-")
    .replace(/^-|-$/g, "")
    .slice(0, maxLength);

const createAttemptId = () => {
  if (typeof window !== "undefined" && window.crypto?.randomUUID) {
    return window.crypto.randomUUID();
  }
  return `${Date.now()}-${Math.random().toString(36).slice(2, 14)}`;
};

const createAttempt = () => ({
  visitorId: createAttemptId(),
  creadoEn: new Date().toISOString(),
});

const attemptKey = (landingSlug) =>
  `${ATTEMPT_KEY_PREFIX}:${cleanPart(landingSlug, 80)}`;

export const getAbandonedCartVisitorId = (landingSlug) => {
  const key = attemptKey(landingSlug);
  const memoryAttempt = memoryAttempts.get(key);
  if (memoryAttempt) return memoryAttempt.visitorId;

  try {
    const savedAttempt = JSON.parse(window.sessionStorage.getItem(key) || "null");
    if (
      /^[a-zA-Z0-9-]{16,44}$/.test(savedAttempt?.visitorId || "") &&
      /^\d{4}-\d{2}-\d{2}T/.test(savedAttempt?.creadoEn || "")
    ) {
      memoryAttempts.set(key, savedAttempt);
      return savedAttempt.visitorId;
    }
  } catch (_) {
    // sessionStorage puede estar desactivado; el respaldo en memoria mantiene
    // la idempotencia durante la visita sin crear una cookie persistente.
  }

  const attempt = createAttempt();
  memoryAttempts.set(key, attempt);
  try {
    window.sessionStorage.setItem(key, JSON.stringify(attempt));
  } catch (_) {}
  return attempt.visitorId;
};

export const getAbandonedCartCreatedAt = (landingSlug) => {
  getAbandonedCartVisitorId(landingSlug);
  return memoryAttempts.get(attemptKey(landingSlug)).creadoEn;
};

export const clearAbandonedCartVisitorId = (landingSlug) => {
  const key = attemptKey(landingSlug);
  memoryAttempts.delete(key);
  try {
    window.sessionStorage.removeItem(key);
  } catch (_) {}
};

export const buildAbandonedCartPayload = ({
  visitorId,
  landingSlug,
  productId,
  productName,
  offerQty,
  offerPrice,
  name,
  phone,
  createdAt,
}) => {
  const payload = {
    visitorId: String(visitorId || "").trim(),
    landingSlug: cleanPart(landingSlug, 80),
    productoId: String(productId || "").trim().slice(0, 100),
    productoNombre: String(productName || "").trim().slice(0, 180),
    ofertaCantidad: Number(offerQty),
    ofertaPrecio: Number(offerPrice),
    nombre: String(name || "").trim().replace(/\s+/g, " ").slice(0, 120),
    telefono: String(phone || "").replace(/\D/g, ""),
    creadoEn: String(createdAt || ""),
  };

  const isValid =
    /^[a-zA-Z0-9-]{16,44}$/.test(payload.visitorId) &&
    payload.landingSlug.length >= 3 &&
    payload.productoId.length >= 1 &&
    payload.productoNombre.length >= 3 &&
    Number.isInteger(payload.ofertaCantidad) &&
    payload.ofertaCantidad >= 1 &&
    payload.ofertaCantidad <= 10 &&
    Number.isFinite(payload.ofertaPrecio) &&
    payload.ofertaPrecio > 0 &&
    payload.ofertaPrecio <= 10000 &&
    payload.nombre.length >= 5 &&
    /^09\d{8}$/.test(payload.telefono) &&
    /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}/.test(payload.creadoEn);

  return isValid ? payload : null;
};

export const abandonedCartDocumentId = ({ landingSlug, visitorId }) => {
  const safeSlug = cleanPart(landingSlug, 80);
  const safeVisitorId = String(visitorId || "").replace(/[^a-zA-Z0-9-]/g, "").slice(0, 44);
  if (!safeSlug || !/^[a-zA-Z0-9-]{16,44}$/.test(safeVisitorId)) {
    throw new Error("Identificador de carrito inválido");
  }
  return `${safeSlug}--${safeVisitorId}`;
};

export const buildAbandonedCartWithdrawal = ({
  visitorId,
  landingSlug,
  productId,
  createdAt,
  updatedAt = new Date().toISOString(),
}) => {
  // También valida el par que forma la clave determinística.
  abandonedCartDocumentId({ landingSlug, visitorId });
  const withdrawal = {
    visitorId: String(visitorId),
    landingSlug: cleanPart(landingSlug, 80),
    productoId: String(productId || "").trim().slice(0, 100),
    estado: "retirado",
    creadoEn: String(createdAt || ""),
    actualizadoEn: String(updatedAt || ""),
  };
  if (
    !withdrawal.productoId ||
    !/^\d{4}-\d{2}-\d{2}T/.test(withdrawal.creadoEn) ||
    !/^\d{4}-\d{2}-\d{2}T/.test(withdrawal.actualizadoEn)
  ) {
    throw new Error("Datos de retiro de carrito inválidos");
  }
  return withdrawal;
};
